import { countOwnedTilesOfKind } from "./buildings";
import type { GameState, PlayerId, Tile, VictoryCondition } from "./types";

export function activeVictoryCondition(
  source: Pick<GameState, "victoryConditions">,
): VictoryCondition {
  return source.victoryConditions?.[0] ?? "rout";
}

export function countOwnedCities(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "city", owner });
}

export function countOwnedMonuments(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "monument", owner });
}

function countMonuments(tiles: readonly Tile[]): number {
  return tiles.filter((tile) => tile.kind === "monument").length;
}

export function ownsAllMonuments({
  tiles,
  owner,
}: {
  tiles: readonly Tile[];
  owner: PlayerId;
}): boolean {
  const total = countMonuments(tiles);
  if (total === 0) return false;
  return countOwnedMonuments(tiles, owner) === total;
}

export type ObjectiveProgress = {
  kind: VictoryCondition;
  label: string;
  p1Count: number;
  p2Count: number;
  target: number | null;
};

export function objectiveDescription(
  source: Pick<GameState, "victoryConditions" | "captureCityTarget">,
): string {
  const kind = activeVictoryCondition(source);
  if (kind === "capture") {
    return `Capture ${source.captureCityTarget ?? 0} cities.`;
  }
  if (kind === "monuments") {
    return "Capture all monuments.";
  }
  return "Destroy all enemy units.";
}

export function objectiveProgress(state: GameState): ObjectiveProgress {
  const kind = activeVictoryCondition(state);
  const label = `Objective: ${objectiveDescription(state).replace(/\.$/, "")}`;
  if (kind === "capture") {
    const target = state.captureCityTarget ?? 0;
    return {
      kind,
      label,
      p1Count: countOwnedCities(state.tiles, "p1"),
      p2Count: countOwnedCities(state.tiles, "p2"),
      target,
    };
  }
  if (kind === "monuments") {
    return {
      kind,
      label,
      p1Count: countOwnedMonuments(state.tiles, "p1"),
      p2Count: countOwnedMonuments(state.tiles, "p2"),
      target: countMonuments(state.tiles),
    };
  }
  return {
    kind,
    label,
    p1Count: 0,
    p2Count: 0,
    target: null,
  };
}
